import { css, type RuleSet } from 'styled-components';
import { ColorNeutralWhite, ColorNordicGrey1, ColorNordicGrey3, Spacing16 } from 'autogen/design-tokens/tokens';
import { breakpoint, constants } from 'variables';

export function verticalEllipsis(lineClamp: number): RuleSet {
    return css`
        display: block;
        display: -webkit-box;
        overflow: hidden;
        text-overflow: ellipsis;
        -webkit-line-clamp: ${lineClamp};
        -webkit-box-orient: vertical;
    `;
}

export const container = css`
    padding: 0 ${constants.gridGutter};
`;

export function row(): RuleSet {
    return css`
        margin-left: -${constants.gridGutter};
        margin-right: -${constants.gridGutter};
    `;
}

export function col(): RuleSet {
    return css`
        padding-right: ${constants.gridGutter};
        padding-left: ${constants.gridGutter};
    `;
}

export function customScrollbar() {
    return css`
        overflow: auto;
        scrollbar-width: thin;
        overscroll-behavior: none;
        -webkit-overflow-scrolling: touch;

        &::-webkit-scrollbar {
            width: 6px;
            height: 6px;
        }

        &::-webkit-scrollbar-track,
        &::-webkit-scrollbar {
            background: ${ColorNeutralWhite};
            border-left: 1px solid ${ColorNordicGrey3};
        }

        &::-webkit-scrollbar-thumb {
            background: ${ColorNordicGrey1};
        }
    `;
}

export function invisibleScrollbar() {
    return css`
        /* Hide scrollbar for Chrome, Safari and Opera */
        &::-webkit-scrollbar {
            display: none;
        }

        /* Hide scrollbar for IE, Edge and Firefox */
        & {
            -ms-overflow-style: none; /* IE and Edge */
            scrollbar-width: none; /* Firefox */
        }
    `;
}

export function addScrollPadding(spacing = Spacing16) {
    return css`
        padding-left: ${spacing};
        padding-right: ${spacing};
        scroll-padding-inline-end: ${spacing};
        scroll-padding-inline-start: ${spacing};
    `;
}

export function removeButtonStyling() {
    return css`
        background: none;
        color: inherit;
        border: none;
        padding: 0;
        font: inherit;
        outline: inherit;
        text-align: inherit;
    `;
}

export function removeListStyling() {
    return css`
        list-style: none;
        margin-bottom: 0;
        padding-left: 0;
    `;
}

export function removeLinkTextDecoration() {
    return css`
        &:hover,
        &:active,
        &:focus {
            text-decoration: none;
        }
    `;
}

export function stripDialogStyling() {
    return css`
        inset-inline: unset;
        margin: unset;
        border: none;
        padding: unset;
        width: unset;
        height: unset;
    `;
}

export function gridWithMaxColumns(maxColumnCount: number, minColumnWidth: string, gap: string): RuleSet {
    const gapCount = maxColumnCount - 1;
    return css`
        --total-gap-width: calc(${gapCount} * ${gap});
        --grid-item--max-width: calc((100% - var(--total-gap-width)) / ${maxColumnCount});

        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(max(${minColumnWidth}, var(--grid-item--max-width)), 1fr));
        grid-gap: ${gap};
    `;
}

export function maxColumnGrid(maxColCount: number, minColSize: string, gap = '0px'): RuleSet {
    return css`
        --grid-max-col-count: ${maxColCount};
        --grid-min-col-size: ${minColSize};
        --grid-gap: ${gap};

        /* --- CALCULATIONS --- */
        --grid-col-size-calc: calc(
            (100% - var(--grid-gap) * (var(--grid-max-col-count) - 1)) / var(--grid-max-col-count)
        );
        --grid-col-min-size-calc: min(100%, max(var(--grid-min-col-size), var(--grid-col-size-calc)));
        /* --- CALCULATIONS --- */

        display: grid;
        gap: var(--grid-gap);

        grid-template-columns: repeat(auto-fit, minmax(var(--grid-col-min-size-calc), 1fr));
    `;
}

export function hideOnMobile() {
    return css`
        display: none;

        ${breakpoint.up('1025px')} {
            display: unset;
        }
    `;
}

export function hideOnDesktop() {
    return css`
        ${breakpoint.up('1025px')} {
            display: none;
        }
    `;
}

export function screenReaderPseudoContent(content: string, placement: 'before' | 'after' = 'before') {
    return css`
        &: ${placement} {
            content: '${content}';
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: -1px;
            overflow: hidden;
            clip: rect(0, 0, 0, 0);
            white-space: nowrap;
            border: 0;
        }
    `;
}
